import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SendIcon from '@mui/icons-material/Send';
import React, { useState } from 'react';
import { BoxAction, Mod, TemplateBBox } from '../../../types';
import { ActionsForm } from './ActionsForm';

interface ActionsPanelProps {
  actions: BoxAction[];
  mods: Mod[];
  bboxes: TemplateBBox[];
  onUpdateAction: (bboxId: number, updates: Partial<BoxAction>) => void;
  onRemoveAction: (bboxId: number) => void;
  onClearAll: () => void;
  onSend: () => void;
  onRunCamera: () => void;
}

export const ActionsPanel: React.FC<ActionsPanelProps> = ({
  actions,
  mods,
  bboxes,
  onUpdateAction,
  onRemoveAction,
  onClearAll,
  onSend,
  onRunCamera,
}) => {
  const [experimental, setExperimental] = useState(localStorage.getItem('experimental') === 'true');

  const toggleExperimental = () => {
    const newValue = !experimental;
    setExperimental(newValue);
    localStorage.setItem('experimental', newValue.toString());
    onClearAll(); // Clear actions when toggling experimental mode
  };

  const getBboxById = (bboxId: number) => {
    return bboxes.find((b) => b.bboxId === bboxId);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">Box Actions ({actions.length})</h3>
        <div className="flex gap-2">
          <label className="inline-flex items-center space-x-2 cursor-pointer">
            <span className="text-sm font-medium text-gray-700">Experimental:</span>
            <input
              type="checkbox"
              className="form-checkbox h-5 w-5 text-blue-500 focus:ring-0"
              checked={experimental}
              onChange={toggleExperimental}
            />
          </label>
          <button
            onClick={onRunCamera}
            className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600"
          >
            <PlayArrowIcon className="w-4 h-4" />
            Run Camera
          </button>
          <button
            onClick={onClearAll}
            className="flex items-center gap-2 px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
          >
            <DeleteOutlineIcon className="w-4 h-4" />
            Clear All
          </button>
          <button
            onClick={onSend}
            disabled={actions.length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50"
          >
            <SendIcon className="w-4 h-4" />
            Send
          </button>
        </div>
      </div>

      {actions.length === 0 ? (
        <p className="text-gray-500 text-center py-8">Click on boxes in the image to add actions</p>
      ) : (
        <div className="space-y-4">
          {actions.map((action, index) => {
            const bbox = getBboxById(action.bboxId);
            const mod = mods.find((m) => m.tagName === action.tagName);
            return (
              <div key={action.bboxId} className="border rounded-lg p-4">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex items-center space-x-4">
                    {bbox?.imageUri && (
                      <img
                        src={bbox.imageUri || ''}
                        alt={`BBox ${bbox?.bboxId}`}
                        className="w-16 h-16 object-cover rounded-md"
                      />
                    )}
                    <div>
                      <h4 className="font-medium">
                        <span className="bg-green-200 text-green-800 py-0.5 px-2 rounded-full text-xs font-medium">
                          #{index + 1}
                        </span>{' '}
                        {bbox?.name || bbox?.upc || `Box ${bbox?.bboxId}`}
                        {bbox?.name && (
                          <span className="text-sm text-gray-500 ml-2">({bbox?.upc})</span>
                        )}
                      </h4>

                      <p className="text-sm text-gray-600">
                        Item Number: {bbox?.itemNumber ?? 'N/A'} | Product ID:{' '}
                        {bbox?.productId ?? 'N/A'} | Bbox ID: {bbox?.bboxId}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => onRemoveAction(action.bboxId)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <CloseIcon className="w-4 h-4" />
                  </button>
                </div>

                <ActionsForm
                  action={action}
                  processingType={mod?.processingType || 'structured'}
                  onUpdateAction={onUpdateAction}
                  experimental={experimental}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
